import React, { useState, useEffect, useMemo, forwardRef } from "react";
import { Box, InputAdornment, TextField, Tooltip } from "@material-ui/core";
import { FastField, getIn } from "formik";
import RequiredLabel from "./RequiredLabel";
import configDefaultForm from "./configDefault";
import ErrorIcon from "./ErrorIcon";
import LinearScaleIcon from "@material-ui/icons/LinearScale";
import useShiftSpaceListener from "./useShiftSpaceListener";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";

const GlobitsTextPassword = (props, ref) => (
  <FastField {...props} name={props.name} shouldUpdate={shouldComponentUpdate}>
    {({ field, meta, form }) => (
      <Component {...props} field={field} meta={meta} ref={ref} />
    )}
  </FastField>
);

const Component = forwardRef((props, ref) => {
  const {
    disabled,
    fullWidth = configDefaultForm.fullWidth,
    label,
    isTextArea,
    name,
    size = configDefaultForm.size,
    description,
    variant = configDefaultForm.variant,
    className = "",
    debounceTime = configDefaultForm.debounceTime,
    notDelay,
    field,
    meta,
    autoCapitalize, //tự động viết hoa chữ đầu
    autoReplaceText, //tự động xóa các kí tự và khoảng trắng 2 đầu chỉ cho phép nhập chữ
    autoReplaceTextAndNumber, //tự động xóa các kí tự và khoảng trắng 2 đầu chỉ cho phép nhập chữ và số
    requiredLabel,
    required,
    onChange,
    readOnly,
    inputProps,
    InputLabelProps,
    onKeyDown,
    placeholder,
    isErrorText,
    handleOpenTemplate,
    rows,
    titleTottip = "Chọn mẫu",
    ...otherProps
  } = props;

  const [value, setValue] = useState(field.value);
  const [openPassword, setOpenPassword] = useState(false);
  const [t, setT] = useState(undefined);

  // console.log({ description });

  const handleChange = (e) => {
    e.persist();
    let value = e.target.value;

    if (autoCapitalize) {
      if (value.charAt(0) === value.charAt(0).toLowerCase()) {
        value = value.replace(
          value.charAt(0),
          value.charAt(0).toLocaleUpperCase()
        );
      }
      value = value.replace(/\s\S/g, (s) => s.toLocaleUpperCase());
      e.target.value = value;
    }

    if (autoReplaceText) {
      value = value.replace(/[^a-zA-Zà-ỹÀ-Ỹ\s]/g, "");
      value = value.replace(/^\s+/g, "");
      e.target.value = value;
    }
    if (autoReplaceTextAndNumber) {
      value = value.replace(/[^a-zA-Zà-ỹÀ-Ỹ\s0-9]/g, "");
      value = value.replace(/^\s+/g, "");
      e.target.value = value;
    }
    setValue(value);
    if (!notDelay) {
      if (t) {
        clearTimeout(t);
      }

      setT(
        setTimeout(() => {
          if (onChange) {
            onChange(e);
          } else {
            field.onChange(e);
          }
        }, debounceTime)
      );
    } else {
      if (onChange) {
        onChange(e);
      } else {
        field.onChange(e);
      }
    }
  };

  useEffect(() => {
    if (field.value !== value) {
      setValue(field.value ?? "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [field.value]);

  useShiftSpaceListener(handleOpenTemplate);

  const displayLabel = useMemo(
    () =>
      label ? (
        <RequiredLabel
          label={label}
          requiredLabel={required || requiredLabel}
        />
      ) : null,
    [label, required, requiredLabel]
  );
  const isError = Boolean(meta && meta.touched && meta.error);

  ref = useShiftSpaceListener(handleOpenTemplate);

  return (
    <>
      {!fullWidth && Boolean(displayLabel) && (
        <label htmlFor={name}>{displayLabel}</label>
      )}
      <TextField
        {...otherProps}
        {...field}
        // ref={ref}
        id={name}
        name={name}
        disabled={disabled}
        fullWidth={fullWidth}
        label={fullWidth && displayLabel ? displayLabel : null}
        multiline={isTextArea}
        size={size}
        type={openPassword ? "text" : "password"}
        variant={variant}
        value={value || ""}
        onChange={handleChange}
        error={isError}
        helperText={fullWidth && isError ? meta.error : ""}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        rows={rows}
        inputProps={{
          readOnly: readOnly,
          ref: ref,
          ...inputProps,
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment
              position="end"
              style={{
                cursor: "pointer",
              }}
              onClick={() => {
                setOpenPassword((prev) => !prev);
              }}
            >
              {openPassword ? <Visibility /> : <VisibilityOff />}
            </InputAdornment>
          ),
        }}
        InputLabelProps={{
          htmlFor: name,
          ...InputLabelProps,
        }}
        className={`input-container ${className} ${readOnly ? "read-only" : ""
          } ${isTextArea ? "isTextArea" : ""} ${isErrorText ? "text-field-error" : ""
          }`}
      ></TextField>
      {isError && !fullWidth && <ErrorIcon helperText={meta.error} />}

      {Boolean(displayLabel) && <p className="mt-1" style={{ fontSize: "13px", color: "#1212bd" }}>{description}</p>}
    </>
  );
});

const shouldComponentUpdate = (nextProps, currentProps) => {
  return (
    nextProps.name !== currentProps.name ||
    nextProps.value !== currentProps.value ||
    nextProps.onChange !== currentProps.onChange ||
    nextProps.label !== currentProps.label ||
    nextProps.required !== currentProps.required ||
    nextProps.requiredLabel !== currentProps.requiredLabel ||
    nextProps.disabled !== currentProps.disabled ||
    nextProps.readOnly !== currentProps.readOnly ||
    nextProps.type !== currentProps.type ||
    nextProps.formik.isSubmitting !== currentProps.formik.isSubmitting ||
    Object.keys(nextProps).length !== Object.keys(currentProps).length ||
    getIn(nextProps.formik.values, currentProps.name) !==
    getIn(currentProps.formik.values, currentProps.name) ||
    getIn(nextProps.formik.errors, currentProps.name) !==
    getIn(currentProps.formik.errors, currentProps.name) ||
    getIn(nextProps.formik.touched, currentProps.name) !==
    getIn(currentProps.formik.touched, currentProps.name)
  );
};

export default React.memo(forwardRef(GlobitsTextPassword));
